import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Table2, LayoutGrid } from "lucide-react";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import EntityForm from "../components/EntityForm";
import KanbanBoard from "../components/KanbanBoard";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useEntity, createItem } from "../hooks/useEntity";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { formatCurrency } from "../lib/utils-format";
import { OPP_STAGES, STAGE_COLORS, OPP_FIELDS } from "../config/recordConfigs";

const COLUMNS = [
  { key: "opportunity_name", label: "Opportunity" },
  { key: "company_name", label: "Company" },
  { key: "stage", label: "Stage", badge: true },
  { key: "estimated_value", label: "Value", type: "currency" },
  { key: "probability", label: "Prob %" },
  { key: "expected_close_date", label: "Close", type: "date" },
];

export default function Opportunities() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [view, setView] = useState("table");
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ownerId, setOwnerId] = useState("all");
  const [owners, setOwners] = useState([]);

  const params = { limit: 200 };
  if (isAdmin && ownerId !== "all") params.owner_id = ownerId;
  const { items, total, loading, reload } = useEntity("opportunities", params);

  useEffect(() => {
    if (isAdmin) api.get("/opportunity-owners").then((r) => setOwners(r.data || [])).catch(() => {});
  }, [isAdmin]);

  const submit = async (form) => {
    setSaving(true);
    try {
      await createItem("opportunities", { ...form, status: form.stage });
      toast.success("Opportunity created");
      setFormOpen(false); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const move = async (id, stage) => {
    try {
      await api.post(`/opportunities/${id}/transition`, { to_stage: stage });
      toast.success(`Moved to ${stage}`);
    } catch (e) {
      toast.error(apiError(e));
    }
    reload();
  };
  const open = (r) => navigate(`/record/opportunities/${r.id}`);

  const totalValue = items.filter((o) => !["Closed Won", "Closed Lost"].includes(o.stage)).reduce((s, o) => s + Number(o.estimated_value || 0), 0);

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
      <PageHeader
        title="Design" count={total} subtitle="Architectural design, drawings, and specifications"
        actions={
          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-border overflow-hidden">
              <button onClick={() => setView("kanban")} className={`px-3 py-2 ${view === "kanban" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="view-kanban-btn"><LayoutGrid className="h-4 w-4" /></button>
              <button onClick={() => setView("table")} className={`px-3 py-2 ${view === "table" ? "bg-primary text-primary-foreground" : "hover:bg-accent"}`} data-testid="view-table-btn"><Table2 className="h-4 w-4" /></button>
            </div>
            <Button onClick={() => setFormOpen(true)} data-testid="new-record-btn"><Plus className="h-4 w-4 mr-1.5" />New</Button>
          </div>
        }
      />

      {isAdmin && (
        <div className="flex gap-3 mb-4">
          <Select value={ownerId} onValueChange={setOwnerId}>
            <SelectTrigger className="w-[220px]" data-testid="owner-filter-select"><SelectValue placeholder="All owners" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All owners</SelectItem>
              {owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      {view === "kanban" ? (
        <KanbanBoard
          columns={OPP_STAGES} items={items} groupKey="stage" onMove={move} colorFor={(c) => STAGE_COLORS[c]}
          renderCard={(it) => (
            <div onClick={() => open(it)}>
              <div className="font-medium text-sm leading-snug truncate">{it.opportunity_name}</div>
              <div className="text-xs text-muted-foreground mt-0.5 truncate">{it.company_name}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-display text-sm font-semibold text-primary">{formatCurrency(it.estimated_value)}</span>
                <span className="text-xs text-muted-foreground">{it.probability}%</span>
              </div>
            </div>
          )}
        />
      ) : (
        <DataTable columns={COLUMNS} items={items} loading={loading} onRowClick={open} emptyLabel="No opportunities" />
      )}

      <EntityForm open={formOpen} onOpenChange={setFormOpen} fields={OPP_FIELDS} initial={null} onSubmit={submit} saving={saving} title="New Opportunity" />
    </div>
  );
}
